import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { NOT_ONLY_WHITESPACE_PATTERN, getValidatorRequired } from '@tablesready/common-ui';
import { PublicWaitlistFieldOptionsTR } from '@shared/restaurant/models';
import { TabValues, PublicPagesSections, PublicPagesModes } from './enums';
import { checkboxDisableValidator } from './public-options-validator';

@Injectable()
export class PublicOptionsService {
  constructor (private readonly formBuilder: UntypedFormBuilder) {}

  createFieldOptionsGroup (options: unknown): UntypedFormGroup {
    return this.formBuilder.group(options);
  }

  createPublicWaitlistForm (): UntypedFormGroup {
    return this.formBuilder.group({
      view: [false],
      add: [false],
      schedule: [[]],
      redirect: [null],
      api: [null],
      apiKey: [null],
      image: [null],
      position: [''],
      selectedPublicPage: [null],
      selectedTab: [TabValues.DIRECT_LINK],
      checkinCap: this.formBuilder.group({
        enabled: [null],
        cap: [null, [getValidatorRequired(), Validators.pattern('^[0-9]*$'), Validators.min(1)]],
        capStart: [null],
        reopenDuration: [null, [getValidatorRequired(), Validators.pattern('^[0-9]*$'), Validators.min(0)]],
        message: [null, getValidatorRequired()],
      }),
      fieldSettings: this.formBuilder.group({ maxSize: [null, [getValidatorRequired(), Validators.min(1)]] }),
      requiredFields: this.requiredFieldsGroup(),
      fieldLabels: this.createFieldLabelsGroup(),
      customText: this.formBuilder.group({
        footer: [null],
        publicPageSection: [PublicPagesSections.Waitlist],
        publicPageMode: [PublicPagesModes.View],
        waitlist: this.getCustomTextFieldsOptions(),
        bookings: this.getCustomTextFieldsOptions(),
        closed: ['', [Validators.required]],
      }),
      customMessage: this.formBuilder.group({
        enabled: [null],
        message: [null],
      }),
      theme: this.createThemeGroup(),
      showUpcomingReservations: [false],
      allowReservationsCheckin: [false],
      showOnlyCheckedInReservations: [false],
      enableReservationsCheckinVerification: [false],
      publicUrl: ['', [
        Validators.required, Validators.maxLength(12), Validators.pattern(/^[a-z0-9-_]+$/),
      ]],
      geofence: this.formBuilder.group({
        enabled: [false, [Validators.required]],
        distance: [1000, [Validators.required, Validators.min(0)]],
        message: ['You must be within {distance} feet to access this page.', [Validators.required]],
        checkin: [false, [Validators.required]],
        waitlist: [false, [Validators.required]],
        reservation: [false, [Validators.required]],
      }),
    });
  }

  createFieldLabelsGroup (): UntypedFormGroup {
    return this.formBuilder.group({
      name: [null, [getValidatorRequired(), Validators.pattern(NOT_ONLY_WHITESPACE_PATTERN)]],
      note: [null, [getValidatorRequired(), Validators.pattern(NOT_ONLY_WHITESPACE_PATTERN)]],
      size: [null, [getValidatorRequired(), Validators.pattern(NOT_ONLY_WHITESPACE_PATTERN)]],
      phone: [null, [getValidatorRequired(), Validators.pattern(NOT_ONLY_WHITESPACE_PATTERN)]],
      deliveryId: [null, [getValidatorRequired(), Validators.pattern(NOT_ONLY_WHITESPACE_PATTERN)]],
      truck: [null, [getValidatorRequired(), Validators.pattern(NOT_ONLY_WHITESPACE_PATTERN)]],
      carrier: [null, [getValidatorRequired(), Validators.pattern(NOT_ONLY_WHITESPACE_PATTERN)]],
    });
  }

  requiredFieldsGroup (): UntypedFormGroup {
    return this.formBuilder.group({
      name: [{
        value: true,
        disabled: true,
      }],
      size: [null],
      phone: [null],
      note: [null],
      resources: [null],
      services: [null],
      deliveryId: [null],
      truck: [null],
      carrier: [null],
    });
  }

  getFieldOptions (requiredFields: AbstractControl): Record<keyof PublicWaitlistFieldOptionsTR, unknown[]> {
    return {
      noteOptions: [null],
      striped: [null],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      first_character: [null],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      size_view: [null],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      size_add: [null, checkboxDisableValidator('size', requiredFields)],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      note_view: [null],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      note_add: [null, checkboxDisableValidator('note', requiredFields)],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      actual_wait: [null],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      estimated_wait: [null],
      phone: [null],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      phone_add: [null, checkboxDisableValidator('phone', requiredFields)],
      columns: [null],
      position: [null],
      upcoming: [null],
      showService: [null],
      showResource: [null],
      showResourcesCheckIn: [
        null,
        checkboxDisableValidator('resources', requiredFields),
      ],
      showServicesCheckIn: [
        null,
        checkboxDisableValidator('services', requiredFields),
      ],
      arrivalTime: [null],
      quotedTime: [null],
    };
  }

  private getCustomTextFieldsOptions(): UntypedFormGroup {
    return this.formBuilder.group({
      view: this.getFieldsForCustomTextOptions(),
      add: this.getFieldsForCustomTextOptions(),
    });
  }

  private getFieldsForCustomTextOptions(): UntypedFormGroup {
    return this.formBuilder.group({
      text: [null],
    });
  }

  private createThemeGroup (): UntypedFormGroup {
    return this.formBuilder.group({
      backgroundColor: [null],
      formBackgroundColor: [null],
      bodyColor: [null],
      header1Color: [null],
      header2Color: [null],
      header3Color: [null],
      footerColor: [null],
      estimatedWaitColor: [null],
      tableHeaderColor: [null],
      addButtonBackgroundColor: [null],
      addButtonColor: [null],
      highlightColor: [null],
    });
  }
}

